import { compareDesc, endOfDay, format, isBefore, isWithinInterval, parseISO, startOfDay, subDays } from 'date-fns';
import { AgentCenterResponse, CallHistoryItem } from 'services/api/AgentCenterAPI';

export type FormatedCallHistoryItem = {
    callId: string;
    contactName: string;
    phoneNumber: string;
    timeOfCall: string;
    duration: string;
    actionTaken: string;
    callDate: string;
    callTime: string;
    name: string;
    at: Date;
};

export const DATE_RANGE_OPTIONS = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'custom', label: 'Custom Range' },
];

export const formatCallHistory = ({
    callHistory,
    dateFilter,
    dateRange,
}: {
    callHistory?: CallHistoryItem[];
    dateFilter: string;
    dateRange: [Date | null, Date | null];
}): FormatedCallHistoryItem[] => {
    if (!callHistory) return [];

    const now = new Date();
    const todayStart = startOfDay(now);
    const yesterdayStart = subDays(todayStart, 1);
    const last7DaysStart = subDays(todayStart, 7);
    const last30DaysStart = subDays(todayStart, 30);

    const filteredCalls = callHistory.filter((call) => {
        const callDate = parseISO(call.callTime);

        if (dateFilter === 'today') {
            return !isBefore(callDate, todayStart);
        }
        if (dateFilter === 'yesterday') {
            return !isBefore(callDate, yesterdayStart) && isBefore(callDate, todayStart);
        }
        if (dateFilter === 'last7days') {
            return !isBefore(callDate, last7DaysStart);
        }
        if (dateFilter === 'last30days') {
            return !isBefore(callDate, last30DaysStart);
        }
        if (dateFilter === 'custom') {
            const [start, end] = dateRange;
            if (start && end) {
                return isWithinInterval(callDate, { start: startOfDay(start), end: endOfDay(end) });
            }
            return Boolean(start && end);
        }

        return true;
    });

    return filteredCalls
        .map((call) => {
            const at = parseISO(call.callTime);
            const timeLabel = format(at, 'p');
            return {
                at,
                callId: call.callId,
                contactName: call.contactName,
                phoneNumber: call.phoneNumber,
                timeOfCall: timeLabel,
                duration: `${call.duration} min`,
                actionTaken: call.actionTaken,
                callDate: format(at, 'P'),
                callTime: timeLabel,
                name: call.contactName,
            };
        })
        .sort((a, b) => compareDesc(a.at, b.at));
};

// Format the metrics for display from agentCenterData
export const formatMetrics = ({ agentCenterData }: { agentCenterData?: AgentCenterResponse }) => {
    if (!agentCenterData) return [];

    const cs = agentCenterData.callSnapshot;
    // Format hours active from minutes
    const hoursActive = Math.floor(cs.hoursActive / 60);
    const minutesActive = cs.hoursActive % 60;
    const hoursActiveFormatted = `${hoursActive}:${minutesActive < 10 ? '0' + minutesActive : minutesActive}`;

    // Format average wait time from seconds
    const avgWaitMin = Math.floor(cs.avgWaitTime / 60);
    const avgWaitSec = cs.avgWaitTime % 60;
    const avgWaitFormatted = `${avgWaitMin}:${avgWaitSec < 10 ? '0' + avgWaitSec : avgWaitSec}`;

    return [
        { title: 'Calls Taken', value: cs.callsTaken.toString() },
        { title: 'Deals Won', value: cs.dealsWon.toString() },
        { title: 'Closing Rate', value: `${cs.closingRate}%` },
        { title: 'Hours Active', value: hoursActiveFormatted },
        { title: 'Avg. Wait Time', value: avgWaitFormatted, highlight: true },
    ];
};
